import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { trackEvent, trackPageView } from "../lib/analytics";
import { ArrowLeft, CheckCircle2, Clock3, CreditCard, Loader2, RefreshCw, TicketPercent, XCircle } from "lucide-react";

const PAYMENT_STATE_COPY = {
  success: {
    title: "Pagamento em confirmação",
    description: "Recebemos seu retorno do checkout. Agora estamos aguardando a confirmação final para liberar o resultado do diagnóstico.",
  },
  cancel: {
    title: "Checkout interrompido",
    description: "O pagamento não foi concluído. Você pode retomar o checkout do mesmo diagnóstico quando quiser.",
  },
  expired: {
    title: "Checkout expirado",
    description: "O link anterior expirou. Gere um novo checkout para seguir com a liberação do diagnóstico.",
  },
  default: {
    title: "Aguardando pagamento",
    description: "Finalize o pagamento deste diagnóstico para liberar o resultado e a análise financeira enviada.",
  },
};

const STATUS_BADGES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
  expired: "bg-slate-100 text-slate-700",
};

const DiagnosticPaymentStatus = () => {
  const { diagnosticId, paymentState } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);

  const stateCopy = useMemo(
    () => PAYMENT_STATE_COPY[paymentState] || PAYMENT_STATE_COPY.default,
    [paymentState]
  );

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/payment-status`, {
        withCredentials: true,
      });
      setStatusData(response.data);

      if (response.data?.result_released) {
        toast.success("Pagamento confirmado. Resultado liberado!");
        navigate(`/results/${diagnosticId}`, { replace: true });
      }
    } catch (error) {
      console.error("Error fetching payment status:", error);
      toast.error("Erro ao consultar o status do pagamento");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [diagnosticId, navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    trackPageView("payment_page_view", {
      path: `/payment/diagnostic/${diagnosticId}`,
      metadata: { diagnostic_id: diagnosticId },
    });
  }, [diagnosticId]);

  useEffect(() => {
    if (!statusData?.payment || statusData.result_released) return undefined;
    const timer = window.setInterval(() => {
      fetchStatus();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [fetchStatus, statusData]);

  useEffect(() => {
    if (statusData?.applied_coupon?.code) {
      setCouponCode(statusData.applied_coupon.code);
    }
  }, [statusData]);

  const regenerateCheckout = async () => {
    setRegenerating(true);
    try {
      trackEvent("checkout_generate_attempt", {
        path: `/payment/diagnostic/${diagnosticId}`,
        metadata: { diagnostic_id: diagnosticId, source: "payment_status_page" },
      });
      const response = await axios.post(`${API}/diagnostics/${diagnosticId}/checkout`, {}, { withCredentials: true });
      if (response.data?.payment?.status === "confirmed") {
        trackEvent("diagnostic_released_by_coupon_client", {
          path: `/results/${diagnosticId}`,
          metadata: { diagnostic_id: diagnosticId },
        });
        toast.success("Diagnóstico liberado pelo cupom aplicado.");
        await fetchStatus();
        return;
      }
      if (response.data?.checkout_url) {
        trackEvent("checkout_opened", {
          path: `/payment/diagnostic/${diagnosticId}`,
          metadata: { diagnostic_id: diagnosticId, checkout_url: response.data.checkout_url },
        });
        window.location.href = response.data.checkout_url;
        return;
      }
      await fetchStatus();
      toast.error("Não foi possível gerar o checkout");
    } catch (error) {
      console.error("Error regenerating checkout:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar checkout");
    } finally {
      setRegenerating(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Informe um cupom para aplicar");
      return;
    }
    setCouponLoading(true);
    try {
      trackEvent("coupon_apply_attempt", {
        path: `/payment/diagnostic/${diagnosticId}`,
        metadata: { diagnostic_id: diagnosticId, coupon_code: couponCode.trim() },
      });
      const response = await axios.post(
        `${API}/diagnostics/${diagnosticId}/coupon`,
        { code: couponCode.trim() },
        { withCredentials: true }
      );
      toast.success("Cupom aplicado com sucesso");
      trackEvent("coupon_apply_success", {
        path: `/payment/diagnostic/${diagnosticId}`,
        metadata: { diagnostic_id: diagnosticId, coupon_code: couponCode.trim() },
      });
      if (response.data?.result_released) {
        navigate(response.data.results_url || `/results/${diagnosticId}`, { replace: true });
        return;
      }
      await fetchStatus();
      await regenerateCheckout();
    } catch (error) {
      console.error("Error applying coupon:", error);
      trackEvent("coupon_apply_failure", {
        path: `/payment/diagnostic/${diagnosticId}`,
        metadata: {
          diagnostic_id: diagnosticId,
          coupon_code: couponCode.trim(),
          reason: error.response?.data?.detail || "unknown",
        },
      });
      toast.error(error.response?.data?.detail || "Erro ao aplicar cupom");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = async () => {
    setCouponLoading(true);
    try {
      await axios.delete(`${API}/diagnostics/${diagnosticId}/coupon`, { withCredentials: true });
      toast.success("Cupom removido");
      setCouponCode("");
      await fetchStatus();
      await regenerateCheckout();
    } catch (error) {
      console.error("Error removing coupon:", error);
      toast.error(error.response?.data?.detail || "Erro ao remover cupom");
    } finally {
      setCouponLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
      </div>
    );
  }

  const payment = statusData?.payment;
  const pricing = statusData?.pricing || {};
  const appliedCoupon = statusData?.applied_coupon;
  const accountantPartnership = statusData?.accountant_partnership;
  const badgeClass = STATUS_BADGES[payment?.status] || "bg-slate-100 text-slate-700";
  const isPending = payment?.status === "pending";
  const baseAmount = Number(pricing?.base_amount ?? payment?.base_amount ?? 19.9);
  const discountAmount = Number(pricing?.discount_amount ?? payment?.discount_amount ?? 0);
  const totalAmount = Number(pricing?.final_amount ?? payment?.amount ?? 19.9);
  const isFreeByCoupon = totalAmount <= 0;
  const currentCouponCode = appliedCoupon?.code || null;
  const paymentCouponCode = payment?.coupon?.code || null;
  const checkoutMatchesCurrentPricing =
    Number(payment?.amount ?? totalAmount) === totalAmount &&
    paymentCouponCode === currentCouponCode;
  const canOpenCheckout =
    Boolean(payment?.checkout_url) &&
    payment?.status !== "confirmed" &&
    !isFreeByCoupon &&
    checkoutMatchesCurrentPricing;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center">
            <BrandLogo size="md" className="max-w-[220px]" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center text-slate-600 hover:text-navy mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao dashboard
        </Link>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="font-serif text-2xl text-navy">{stateCopy.title}</CardTitle>
                <CardDescription className="mt-2">{stateCopy.description}</CardDescription>
              </div>
              {payment?.status === "confirmed" ? (
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              ) : payment?.status === "cancelled" || payment?.status === "expired" ? (
                <XCircle className="h-10 w-10 text-rose-600" />
              ) : (
                <Clock3 className="h-10 w-10 text-amber-500" />
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Diagnóstico</p>
                <p className="mt-2 text-sm font-semibold text-navy">{diagnosticId}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Valor</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-500">Original: R$ {baseAmount.toFixed(2).replace(".", ",")}</p>
                  {discountAmount > 0 && (
                    <p className="text-xs text-emerald-700">
                      Desconto: - R$ {discountAmount.toFixed(2).replace(".", ",")}
                    </p>
                  )}
                  <p className="text-sm font-semibold text-navy">
                    Total: R$ {totalAmount.toFixed(2).replace(".", ",")}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
                <Badge className={`mt-2 ${badgeClass}`}>{payment?.status_label || "Aguardando pagamento"}</Badge>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-navy flex items-center gap-2">
                    <TicketPercent className="h-4 w-4" />
                    Cupom de desconto
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Se você tiver um cupom, aplique antes de abrir o checkout.</p>
                </div>
                {appliedCoupon?.code && (
                  <Badge className="bg-emerald-100 text-emerald-800">{appliedCoupon.code}</Badge>
                )}
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <input
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Digite o código do cupom"
                  disabled={couponLoading || payment?.status === "confirmed"}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={applyCoupon}
                  disabled={couponLoading || payment?.status === "confirmed"}
                >
                  {couponLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TicketPercent className="h-4 w-4 mr-2" />}
                  Aplicar cupom
                </Button>
                {appliedCoupon?.code && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={removeCoupon}
                    disabled={couponLoading || payment?.status === "confirmed"}
                  >
                    Remover cupom
                  </Button>
                )}
              </div>

              {appliedCoupon?.code && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <p className="text-sm font-medium text-emerald-800">
                    Cupom {appliedCoupon.code} aplicado com sucesso.
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    Desconto atual: R$ {Number(appliedCoupon.discount_amount || 0).toFixed(2).replace(".", ",")}
                  </p>
                </div>
              )}
            </div>

            {accountantPartnership?.accountant_id && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
                <p className="text-sm font-medium text-sky-900">Diagnóstico vinculado a parceria contábil.</p>
                <p className="text-xs text-sky-800 mt-1">
                  Benefício desta parceria: {Number(accountantPartnership.discount_percent || 0) > 0
                    ? `${accountantPartnership.discount_percent}% de desconto`
                    : "sem desconto nesta etapa"}.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-700">
                {statusData?.journey_stage?.label || "Aguardando pagamento para liberar o resultado do diagnóstico."}
              </p>
              {isFreeByCoupon && payment?.status !== "confirmed" && (
                <p className="mt-2 text-xs text-emerald-700">
                  Este cupom zerou o valor do diagnóstico. Clique em atualizar status para liberar o resultado caso ele ainda não tenha sido aberto automaticamente.
                </p>
              )}
              {!isFreeByCoupon && isPending && !checkoutMatchesCurrentPricing && (
                <p className="mt-2 text-xs text-amber-700">
                  O checkout anterior não corresponde mais ao valor atual com desconto. Gere um novo checkout para continuar com o valor corrigido.
                </p>
              )}
              {isPending && (
                <p className="mt-2 text-xs text-slate-500">
                  Esta tela se atualiza automaticamente. Assim que o Asaas confirmar o pagamento, o resultado será liberado.
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              {canOpenCheckout && (
                <Button className="bg-navy hover:bg-navy/90" onClick={() => (window.location.href = payment.checkout_url)}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Abrir checkout
                </Button>
              )}
              <Button variant="outline" onClick={fetchStatus}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar status
              </Button>
              {(payment?.status === "expired" || payment?.status === "cancelled" || !payment?.checkout_url || !checkoutMatchesCurrentPricing) && (
                <Button variant="outline" onClick={regenerateCheckout} disabled={regenerating}>
                  {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                  Gerar novo checkout
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DiagnosticPaymentStatus;
